import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { FormikErrors, FormikValues } from 'formik';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useContext, useId, useState } from 'react';
import IssuerModal from '@/modules/common/components/modals/IssuerModal.tsx';
import '@/modules/common/styles/common.css';
import { AdminIssuerItem } from '@/modules/admin-dashboard/components/items/AdminIssuerItem.tsx';
import {
  useAdminInviteIssuerMutation,
  useAdminIssuerListQuery,
} from '@/modules/admin-dashboard/queries/issuers.tsx';
import { useAdminRegisterListQuery } from '@/modules/admin-dashboard/queries/registers.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import {
  Button,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  SelectPaginate,
  SelectPaginateOption,
  Spinner,
} from 'ui';
import { GetIssuersV1UsersIssuersGetParams } from 'api';

type issuersFilters = Omit<GetIssuersV1UsersIssuersGetParams, 'page_size' | 'page'>;

const AdminIssuers = () => {
  const [filters, setFilters] = useState<issuersFilters>({});

  const {
    data: issuerList,
    fetchNextPage: fetchNextIssuerListPage,
    hasNextPage: issuerListHasNextPage,
    isLoading: isIssuerListLoading,
  } = useAdminIssuerListQuery({ page: 1, page_size: 15, ...filters });

  const { fetchNextPage: fetchNextRegisterListPage } = useAdminRegisterListQuery(
    { page: 1, page_size: 15 },
    { cacheKey: useId(), disabled: true },
  );

  const { openModal } = useContext(ModalProviderContext);

  const handleAsyncRegisters = useCallback(
    async (currentPage: number, search?: string) => {
      const response = await fetchNextRegisterListPage({
        pageParam: { page: currentPage, search },
      });

      return response.data?.pages.reduce(
        (acc: any, { data, page }) =>
          page === currentPage ? data.map(({ name, id }) => ({ label: name, value: id })) : acc,
        [],
      );
    },
    [fetchNextRegisterListPage],
  );

  const handleSelectRegisterFilter = useCallback((option: SelectPaginateOption<number> | null) => {
    setFilters(filters =>
      option ? { ...filters, register_id: option.value } : omit(filters, 'register_id'),
    );
  }, []);

  const handleOpenInviteIssuerModal = useCallback(() => {
    openModal(
      IssuerModal<{
        setErrors?: (errors: FormikErrors<FormikValues>) => void;
      }>,
      { mutation: [useAdminInviteIssuerMutation, {}] },
    );
  }, [openModal]);

  const issuersLength = (issuerList?.pages || []).flatMap(({ data }) => data).length;

  const headerAction = (
    <div className="flex gap-2 flex-wrap [&>*]:min-w-[230px] [&>*]:w-fit [&>*]:max-w-[300px] [&>*]:flex-1">
      <SelectPaginate
        isClearable
        placeholder="Select register"
        onAsync={handleAsyncRegisters}
        onChange={handleSelectRegisterFilter}
      />
      <SearchField
        onChange={search =>
          setFilters(filters => (search ? { ...filters, search } : omit(filters, 'search')))
        }
      />
      <Button className="w-[180px] shrink-0" onClick={handleOpenInviteIssuerModal}>
        Add new issuer
      </Button>
    </div>
  );

  return (
    <PageLayout title="Issuers" headerAction={headerAction}>
      <EmptyListState
        title="No issuers yet"
        description="You can create issuers from here"
        isLoading={isIssuerListLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={issuersLength}
      />
      {isIssuerListLoading && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <InfiniteScroll
        next={fetchNextIssuerListPage}
        dataLength={issuerList?.pages.length ?? 0}
        hasMore={issuerListHasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col space-y-2">
          {issuerList?.pages.map(({ data }) =>
            data.map(issuer => <AdminIssuerItem key={issuer.id} issuer={issuer} />),
          )}
        </div>
      </InfiniteScroll>
    </PageLayout>
  );
};

export default withPrivateAccess(AdminIssuers, Role.SR_ADMIN);
